import React, { useEffect, useRef, useState } from 'react';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { HeartIcon } from '@heroicons/react/24/solid';
import { useBrandTokenStyle } from '@charitystack/design-tokens/provider';
import { buttonVariants, useBrandColors, safePostMessageToParent } from '@charitystack/shared';
import { getEnvironment, buildApiUrl } from './config/env';
import { fetchElementsConfigOnce, readSharedJson } from './Util/elementsConfigCache';
import SupportersListSkeleton from './Util/SupportersListSkeleton';
import { donorRowMoney } from './Util/formatUtils';

// Initialize the countries database
countries.registerLocale(enLocale);

// The backend's page window: `elements-config?page=N` returns the newest N * 20 donors
// (cumulative, not a slice), so a fuller response than the last one means more may remain.
const DONOR_PAGE_SIZE = 20;

function DonorList({
  donorListID,
  brandColors: propBrandColors,
  previewMode = false,
  isStandAlone = false,
  donorListType = 'RECENT_DONORS',
  connectedForms = [],
  donorsList = [],
  donorListData = null, // Donor list data from API response (recentDonors, topDonors)
  showAllSupportersAnonymous: showAllSupportersAnonymousProp = false,
  // CON-529 F6 (Paper 84A-0 / 8AX-0): inside the supporters modal the rows sit
  // straight on the modal surface, so the list draws no card of its own.
  bare = false,
}) {
  // CON-803 (reverses the list half of CON-750): each row shows what that donor actually paid,
  // in their own currency, from the row's displayCurrency/displayAmount. It never follows the
  // visitor's display currency, so two rows can show two currencies.
  function addCommas(number) {
    // Convert to number if it's a string
    const num = typeof number === 'string' ? parseFloat(number) : number;

    if (typeof num !== 'number' || isNaN(num)) {
      console.error('addCommas received invalid input:', number);
      return '0';
    }

    // Convert the number to a string
    let numberStr = num.toString();

    // Split the number into integer and decimal parts
    let [integerPart] = numberStr.split('.');

    // Add commas to the integer part
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return integerPart;
  }

  // Enhanced UI utility functions (similar to SupportersList)
  function formatTimeAgo(timestamp) {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInWeeks = Math.floor(diffInDays / 7);

    if (diffInSeconds < 60) {
      return 'less than a minute ago';
    } else if (diffInMinutes === 1) {
      return '1 min ago';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} mins ago`;
    } else if (diffInHours === 1) {
      return '1 hour ago';
    } else if (diffInHours < 24) {
      return `${diffInHours} hours ago`;
    } else if (diffInDays === 1) {
      return 'yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else if (diffInWeeks === 1) {
      return '1 week ago';
    } else {
      return `${diffInWeeks} weeks ago`;
    }
  }

  const getCountryCode = (location) => {
    if (!location) return null;

    const country = location.split(',').pop().trim();

    // Use i18n-iso-countries for comprehensive mapping
    const countryCode = countries.getAlpha2Code(country, 'en');

    // Fallback to manual mapping for edge cases
    if (!countryCode) {
      const manualMapping = {
        'United States': 'US',
        USA: 'US',
        'United Kingdom': 'GB',
        UK: 'GB',
        'Korea, North': 'KP',
        'Korea, South': 'KR',
        'East Timor': 'TL',
        'Vatican City': 'VA',
        // PE-1878: defensive pin — getAlpha2Code('Palestine', 'en') currently
        // resolves to 'PS' on i18n-iso-countries 7.14.0 (so this entry is not
        // hit today), but the canonical label is kept here in case the
        // library's alias table changes in a future version.
        Palestine: 'PS',
      };
      const manualResult = manualMapping[country] || null;
      return manualResult;
    }

    return countryCode;
  };

  const getFlagEmoji = (countryCode) => {
    if (!countryCode) return null;

    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map((char) => 127397 + char.charCodeAt());

    const result = String.fromCodePoint(...codePoints);
    return result;
  };

  const [hostedPage, setHostedPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(false);
  const [elementID, setElementID] = useState('');
  const [listType, setListType] = useState('RECENT_DONORS');
  const [donations, setDonations] = useState([]);
  const [hasManyForms, setHasManyForms] = useState(false);
  const [domID, setDomID] = useState('');

  // Store both donor lists for preview mode switching
  const [topDonorsList, setTopDonorsList] = useState([]);
  const [recentDonorsList, setRecentDonorsList] = useState([]);

  // "Show more": loadingMore disables the button while a page is in flight, hasMore decides
  // whether it renders.
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  // Index of the first donor added by the latest "Show more" (null until one is tapped), so
  // only the newly arrived cards ease in; the first window renders still, as it always did.
  const [revealFrom, setRevealFrom] = useState(null);

  // Configuration data state
  const [configData, setConfigData] = useState({});

  // NEW: Use shared brand colors hook (SAME as StandardForm pattern)
  const { brandColors } = useBrandColors(
    configData?.lightColor || '#66b089',
  );

  // Use propBrandColors when available (preview mode OR when passed from parent), otherwise use generated brandColors
  const effectiveBrandColors = propBrandColors && Object.keys(propBrandColors).length > 0 ? propBrandColors : brandColors;

  // Brand reaches the empty-state line and the "Show more" link through the
  // brand TEXT role, written inline on this list's own root (G3H-0 draws both in
  // brand on the standalone embed too). In the element builder's preview
  // (`isStandAlone`) nothing is written and the role falls back to the ink
  // (`var(--cs-brand-text, var(--cs-text-primary))`).
  const brandTokenStyle = useBrandTokenStyle({
    palette: isStandAlone ? undefined : effectiveBrandColors,
  });

  // Refs for pagination tracking (similar to SupportersList)
  const pageRef = useRef(1);
  const donationsLengthRef = useRef(0);
  const hasInitialized = useRef(false);

  // Frequency mapping for recurring donations
  const freqMap = {
    '': '',
    DAILY: 'Daily',
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
    ANNUALLY: 'Yearly',
  };

  // Generate dummy donor data for preview mode
  const dummyDonors = React.useMemo(() => {
    if (!previewMode) return [];

    const formNames = ['General Donation', 'Annual Campaign', 'Emergency Relief', 'Education Fund'];

    const donors = [
      {
        name: 'John Smith',
        grossAmount: 250,
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
        frequency: 'MONTHLY',
        city: 'New York',
        country: 'United States',
        anonymous: false,
        automatedDonation: false,
        formName: hasManyForms ? formNames[0] : '',
      },
      {
        name: 'Jane Doe',
        grossAmount: 150,
        timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(), // 5 hours ago
        frequency: '',
        city: 'Los Angeles',
        country: 'United States',
        anonymous: false,
        automatedDonation: false,
        formName: hasManyForms ? formNames[1] : '',
      },
      {
        name: 'Michael Johnson',
        grossAmount: 500,
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
        frequency: 'MONTHLY',
        city: 'Chicago',
        country: 'United States',
        anonymous: false,
        automatedDonation: false,
        formName: hasManyForms ? formNames[2] : '',
      },
      {
        name: 'Sarah Williams',
        grossAmount: 100,
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
        frequency: 'WEEKLY',
        city: 'Houston',
        country: 'United States',
        anonymous: false,
        automatedDonation: false,
        formName: hasManyForms ? formNames[3] : '',
      },
    ];

    // Sort based on donor list type
    if (donorListType === 'TOP_DONORS') {
      // Sort by amount (highest first)
      return donors.sort((a, b) => b.grossAmount - a.grossAmount);
    } else {
      // Recent donors - already in chronological order
      return donors;
    }
  }, [previewMode, donorListType, hasManyForms]);

  // create an async useEffect to fetch elements config
  // Resolves true when the page was applied, false when the request failed.
  const fetchElementsConfig = async () => {
    try {
      // Grab elements config using elementID in query params
      // if elementID is not in query params, use the prop or fallback
      let elementID = donorListID;

      if (elementID == null) {
        const environment = getEnvironment();
        elementID =
          environment == 'development'
            ? 'dd85afac-0cc8-4a01-88ea-72cfd862cf86'
            : '318bfedb-b2ca-497a-8b7f-9d0cccf6fb0c';
      }
      setElementID(elementID);

      const apiUrl = buildApiUrl(`/elements-config`, {
        elementID: elementID,
        page: pageRef.current,
      });

      // CON-75: the hosted page keeps both layout branches in the React tree, so
      // this component mounts twice and used to ask for page 1 twice at once.
      // The url carries the page number, so later pages key separately and
      // paginate exactly as before.
      const response = await fetchElementsConfigOnce(apiUrl);

      if (!response.ok) {
        throw new Error(
          `API request failed with status ${response.status}: ${response.statusText}`,
        );
      }

      // The Response is shared with any concurrent mount; clone before reading.
      const data = await readSharedJson(response);

      if (data.elementType != 'DONOR_LIST') {
        throw new Error('Wrong element type');
      }

      // Store both lists for preview mode switching
      if (data.topDonors) {
        setTopDonorsList(data.topDonors);
      }
      if (data.recentDonors) {
        setRecentDonorsList(data.recentDonors);
      }

      // Enhanced data processing with priority-based selection
      let donationsArray = [];

      // Use donorListType prop in preview mode, otherwise use data.listType from API
      const effectiveListType = previewMode ? donorListType : data.listType;

      // PRIORITIZE recentDonors/topDonors over data.donations
      if (
        (effectiveListType === 'recent' || effectiveListType === 'RECENT_DONORS') &&
        data.recentDonors &&
        data.recentDonors.length > 0
      ) {
        donationsArray = data.recentDonors;
      } else if (
        (effectiveListType === 'top' || effectiveListType === 'TOP_DONORS') &&
        data.topDonors &&
        data.topDonors.length > 0
      ) {
        donationsArray = data.topDonors;
      } else if (data.donations && data.donations.length > 0) {
        donationsArray = data.donations;
      } else {
        console.log('❌ No donations array found or all arrays are empty');
      }

      // Decide whether "Show more" renders. A backend that reports the total is exact.
      // Otherwise a full window (page * 20 donors) may have more behind it, but only if
      // this page grew the list: a backend that ignores `page` returns the same donors
      // every time, and the button must not offer to load them again.
      const reportedTotal = Number.isFinite(data.totalDonors) ? data.totalDonors : null;
      const listGrew =
        pageRef.current === 1 || donationsArray.length > donationsLengthRef.current;
      const moreAvailable =
        reportedTotal !== null
          ? donationsArray.length < reportedTotal
          : donationsArray.length >= pageRef.current * DONOR_PAGE_SIZE;
      setHasMore(!previewMode && listGrew && moreAvailable);
      donationsLengthRef.current = donationsArray.length;

      if (data.formIDs) {
        setHasManyForms(data.formIDs.length > 1);
      }
      setActive(data.active);
      setDonations(previewMode ? donationsArray.splice(0, 4) : donationsArray);
      setListType(effectiveListType);

      setConfigData(data); // Store full config (SAME as StandardForm pattern)

      setLoading(false);
      setLoadingMore(false);

      // Trigger iframe resize after data is loaded and rendered
      setTimeout(() => {
        resizeIframe();
      }, 100);
      return true;
    } catch (err) {
      console.error('❌ DonorList: Error in fetchElementsConfig:', err);
      setLoading(false);
      setLoadingMore(false);
      return false;
    }
  };

  // Ref to track if we're currently fetching to prevent race conditions
  const isFetchingRef = useRef(false);

  // Manual resize function to send postMessage to parent iframe
  const resizeIframe = () => {
    // Skip resize in preview mode (no iframe to resize)
    if (previewMode) return;

    // Don't send if IDs not set yet
    if (!elementID || !domID) return;

    const height = document.body.scrollHeight;
    const width = document.body.scrollWidth;

    safePostMessageToParent(
      {
        action: 'RESIZE',
        entity: 'CHARITYSTACK',
        sender: 'DONOR_LIST',
        receiver: 'HOSTED_SCRIPT',
        data: JSON.stringify({
          height: height,
          width: width,
          elementID: elementID,
          domID: domID,
        }),
      },
      '*',
    );
  };

  // "Show more": fetch the next window and let the list grow. This replaced scroll-triggered
  // loading, which could never fire in an embed: the iframe is resized to its full content
  // height, so it has nothing to scroll and the parent page does the scrolling.
  const loadMore = async () => {
    if (previewMode || isFetchingRef.current) return;

    isFetchingRef.current = true; // block a double tap before state re-renders
    setLoadingMore(true);
    setRevealFrom(donationsLengthRef.current); // set before the donors arrive so they mount animated
    pageRef.current += 1;
    const applied = await fetchElementsConfig();
    if (!applied) {
      pageRef.current -= 1; // let the next tap retry the same page
      setRevealFrom(null);
    }
    isFetchingRef.current = false;
  };

  // Handle preview mode updates when donorListType or donorListData changes
  useEffect(() => {
    if (previewMode) {
      // Priority 1: Use donorListData from form response (after save)
      if (donorListData) {
        if (donorListType === 'TOP_DONORS' && donorListData.topDonors?.length > 0) {
          setDonations(donorListData.topDonors.slice(0, 4));
          setListType('TOP_DONORS');
          setTopDonorsList(donorListData.topDonors);
        } else if (donorListData.recentDonors?.length > 0) {
          setDonations(donorListData.recentDonors.slice(0, 4));
          setListType('RECENT_DONORS');
          setRecentDonorsList(donorListData.recentDonors);
        }
        // Store both lists for switching
        if (donorListData.topDonors) setTopDonorsList(donorListData.topDonors);
        if (donorListData.recentDonors) setRecentDonorsList(donorListData.recentDonors);
        setActive(true);
        setLoading(false);
        return;
      }

      // Priority 2: When donorListID is null, use dummy data
      if (donorListID == null) {
        setDonations(dummyDonors);
        setListType(donorListType);
        setActive(true);
        setLoading(false);
        return;
      }

      // Priority 3: When donorListID exists and we have stored lists, switch between them
      if (donorListType === 'TOP_DONORS' && topDonorsList.length > 0) {
        setDonations(topDonorsList.slice(0, 4));
        setListType('TOP_DONORS');
      } else if (donorListType === 'RECENT_DONORS' && recentDonorsList.length > 0) {
        setDonations(recentDonorsList.slice(0, 4));
        setListType('RECENT_DONORS');
      }
    }
  }, [previewMode, donorListID, donorListType, donorListData, dummyDonors, topDonorsList, recentDonorsList]);

  useEffect(() => {
    // Skip API fetch in preview mode without donorListID
    if (previewMode && donorListID == null) {
      return;
    }
    // Prevent multiple API calls (React Strict Mode protection)
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    fetchElementsConfig().catch((err) => {
      console.error('❌ DonorList: Initial fetchElementsConfig failed:', err);
      setLoading(false); // Set loading to false even on error
    });

    // grab hostedPage boolean
    const urlParams = new URLSearchParams(window.location.search);
    const hostedPage = urlParams.get('page');
    // hostedPage: null (embedded) or truthy value (hosted page)
    setHostedPage(hostedPage);
    setDomID(urlParams.get('domID'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Handle standalone mode with props data (CreateDonorListDrawer)
    if (isStandAlone) {
      if (donorListID == null && donorsList.length === 0) {
        // No donorListID and no donors - show dummy data
        setDonations(dummyDonors);
        setListType(donorListType);
        setActive(true);
        setLoading(false);
        return;
      }
      if (donorsList && donorsList.length > 0) {
        // Have donors from props - use them
        setDonations(donorsList);
        setListType(donorListType);
        setHasManyForms(connectedForms.length > 1);
        setActive(true);
        setLoading(false);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStandAlone, donorsList, donorListType, donorListID]);

  // Send FINISHED_LOADING message when component finishes loading
  useEffect(() => {
    if (loading === false && hostedPage !== null) {
      // send message that element has finished loading
      safePostMessageToParent(
        {
          action: 'FINISHED_LOADING',
          entity: 'CHARITYSTACK',
          sender: 'DONOR_LIST',
          receiver: 'HOSTED_PAGE',
        },
        '*',
      );
    }
  }, [loading, hostedPage]);

  // Color generation now handled automatically by useBrandColors hook
  // propBrandColors handling removed - useBrandColors hook handles all color generation

  // Trigger resize whenever donations list changes (for pagination/infinite scroll)
  useEffect(() => {
    if (!loading && donations.length > 0 && elementID && domID) {
      setTimeout(() => {
        resizeIframe();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donations, loading, elementID, domID]);

  if (loading) {
    // The same frame as the Suspense fallbacks in front of this component
    // (NonFormSkeleton, hosted SupportersListBooting), so the hand-over is seamless.
    return <SupportersListSkeleton />;
  }

  if (!active) {
    return null;
  }

  // G3H-0: one card (surface, 1px edge, card radius, card shadow) holding the
  // supporter rows, each divided by the subtle edge; "Show more" is a brand
  // text link at the end. Same row as the hosted supporters modal.
  // `bare` (the supporters modal) keeps the rows and drops the card and its inset.
  const cardClassName = bare
    ? 'w-full'
    : 'w-full rounded-[var(--cs-card-radius)] border border-[color:var(--cs-border-control)] bg-[color:var(--cs-surface-0)] shadow-[var(--cs-card-shadow)]';
  const cardInsetX = bare ? '' : 'px-[var(--cs-space-5)]';

  return (
    <div style={brandTokenStyle}>
      {donations.length === 0 ? (
        <div className={`${cardClassName} flex flex-col items-center ${cardInsetX} py-[var(--cs-space-6)]`}>
          <div className="flex items-center gap-[var(--cs-space-1)] text-[length:var(--cs-text-sm-plus)] font-medium leading-[var(--cs-leading-22)] text-[color:var(--cs-brand-text,var(--cs-text-primary))]">
            <span>Be the first to support!</span>
            <HeartIcon aria-hidden="true" className="size-[var(--cs-icon-sm)]" />
          </div>
        </div>
      ) : (
        <div className={`${cardClassName} flex flex-col ${cardInsetX} py-[var(--cs-space-2)]`}>
          {donations.map((donation, index) => {
            const name = typeof donation.name === 'string' ? donation.name.trim().split(/\s+/u)[0] : '';
            // This is public: use only a first name, never a contact identifier.
            const publicName = /\p{L}/u.test(name.replace(/\p{Default_Ignorable_Code_Point}/gu, '')) && !/[@\p{N}]/u.test(name)
              ? name
              : 'Anonymous';
            let timestamp = donation.timestamp;
            const money = donorRowMoney(donation, donation.grossAmount);
            let anonymous = donation.anonymous;
            let automatedDonation = donation.automatedDonation;
            let frequency = freqMap[donation.frequency] || 'One-Time';
            let formName = donation.formName || '';
            let location = '';
            if (donation.city) {
              if (!donation.country) {
                location = donation.city;
              } else {
                location = `${donation.city}, ${donation.country}`;
              }
            } else if (donation.country) {
              location = donation.country;
            }

            const effectiveShowAnonymous = configData?.showAllSupportersAnonymous ?? showAllSupportersAnonymousProp;
            const displayName = effectiveShowAnonymous
              ? 'Someone'
              : anonymous
                ? 'Anonymous'
                : publicName;
            const isRecurring = frequency !== 'One-Time';

            const countryCode = getCountryCode(location);

            const flagEmoji = countryCode ? getFlagEmoji(countryCode) : null;
            const showTime = listType === 'RECENT_DONORS' && Boolean(timestamp);

            // Rows that arrived with the latest "Show more" ease in one after another
            // (a fifth of --cs-motion-fast per step, capped so a big page doesn't drag).
            const isNewlyRevealed = revealFrom !== null && index >= revealFrom;
            const revealStep = Math.min(index - (revealFrom ?? 0), 8);
            const isLast = index === donations.length - 1;

            return (
              <div
                key={index}
                className={`flex w-full items-center gap-[var(--cs-space-3)] py-[var(--cs-space-3)]${
                  isLast ? '' : ' border-b border-[color:var(--cs-border-control-subtle)]'
                }${isNewlyRevealed ? ' animate-fade-in' : ''}`}
                style={
                  isNewlyRevealed
                    ? {
                        animationDuration: 'var(--cs-motion-slow)',
                        animationTimingFunction: 'var(--cs-ease-emphasized)',
                        // cs-allow-hardcoded: a fifth of --cs-motion-fast per step via calc(); the scanner only recognises a value that starts with var(--cs-
                        animationDelay: `calc(var(--cs-motion-fast) * ${revealStep} / 5)`,
                        animationFillMode: 'backwards',
                      }
                    : undefined
                }
              >
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="text-[length:var(--cs-text-sm-plus)] font-medium leading-[var(--cs-leading-22)] text-[color:var(--cs-text-primary)]">
                    <span>{displayName}</span>{' '}
                    {listType === 'TOP_DONORS' ? (
                      'donated'
                    ) : !isRecurring || automatedDonation ? (
                      'donated once'
                    ) : (
                      <>started donating {frequency?.toLowerCase()}</>
                    )}
                    {hasManyForms && formName && <span> to {formName}</span>}
                  </div>
                  {(showTime || location) && (
                    <div className="text-[length:var(--cs-text-xs-plus)] leading-[var(--cs-leading-18)] text-[color:var(--cs-text-tertiary)]">
                      {showTime && <span>{formatTimeAgo(timestamp)}</span>}
                      {showTime && location && <span aria-hidden="true"> · </span>}
                      {location && (
                        <span>
                          {flagEmoji && (
                            <span role="img" aria-label={`${location} flag`}>
                              {flagEmoji}{' '}
                            </span>
                          )}
                          {location}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <span className="shrink-0 text-[length:var(--cs-text-sm-plus)] font-semibold leading-[var(--cs-leading-22)] text-[color:var(--cs-text-primary)]">
                  {money.prefix}
                  {addCommas(money.amount)}
                </span>
              </div>
            );
          })}

          {hasMore && (
            <div className="flex h-[var(--cs-size-button-sm)] box-content items-center justify-center pb-[var(--cs-space-3)] pt-[var(--cs-space-2)]">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                aria-busy={loadingMore}
                // G3H-0: a brand text link (always underlined), 14/20 medium, in a
                // 36-tall row.
                className={`${buttonVariants({ variant: 'link' })} bg-transparent text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)]`}
              >
                Show more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DonorList;
